/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { Grid, IconButton, SelectField, SelectOption, TextField } from '@coursera/cds-core';

import { isValidUrl } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/additionalInfoUtils';
import type { ExternalLinksOption } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/additionalInfoUtils';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/account-profile';

type Props = {
  options: Array<ExternalLinksOption>;
  selectedOption: ExternalLinksOption;
  onSelect: (currentIndex: number, newIndex: number, oldLinkValue?: string) => void;
  onRemove: (index: number) => void;
  onValueChange: (value: string, index: number) => void;
  hideRemove?: boolean;
};

const styles = {
  hideChildLabel: css`
    // Hide the default label for each field, added a custom label for all items
    label {
      display: none;
    }
  `,
  inputFieldContainer: () => css`
    padding: 0 var(--cds-spacing-50) 0 var(--cds-spacing-200);
  `,
  deleteIcon: () => css`
    margin-top: var(--cds-spacing-200);
  `,
};

const ExternalLinksInput = ({ options, selectedOption, onSelect, onRemove, onValueChange, hideRemove }: Props) => {
  const [value, setValue] = useState(selectedOption.value || '');
  const [hasError, setHasError] = useState(false);
  const displayedOptions = [selectedOption].concat(options).sort((a, b) => a.index - b.index);

  const handleSelect = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    onSelect(selectedOption.index, parseInt(e.target.value as string, 10), value);
  };

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onRemove(selectedOption.index);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: newValue } = e.target;
    setHasError(!!newValue && !isValidUrl(newValue));
    setValue(newValue);
    onValueChange(newValue, selectedOption.index);
  };

  return (
    <Grid container alignItems="flex-end">
      <Grid item xs={4} sm={4}>
        <SelectField
          id="website-type"
          optional
          fullWidth
          value={selectedOption.index}
          onChange={handleSelect}
          aria-label={_t('Website Type: #{websiteType}', { websiteType: selectedOption.visibleLabel })}
          css={styles.hideChildLabel}
        >
          {displayedOptions.map((websiteOption) => (
            <SelectOption key={websiteOption.index} value={websiteOption.index}>
              {websiteOption.visibleLabel}
            </SelectOption>
          ))}
        </SelectField>
      </Grid>
      <Grid item xs={7} sm={7} css={styles.inputFieldContainer}>
        <TextField
          optional
          id={`website-input-${selectedOption.id}`}
          name={selectedOption.inputName}
          label={_t('Website Address')}
          type="text"
          value={value}
          fullWidth
          onChange={handleChange}
          css={styles.hideChildLabel}
          inputProps={{
            className: 'fs-mask',
          }}
          placeholder={selectedOption.placeholder || 'https://'}
          validationStatus={hasError ? 'error' : undefined}
        />
      </Grid>
      {!(hideRemove && !value.length) && (
        <Grid item xs={1} sm={1}>
          <IconButton
            variant="ghost"
            intent="delete"
            onClick={handleRemove}
            aria-label={_t('Remove website URL')}
            css={styles.deleteIcon}
            component={TrackedButton}
            // not tracked in eventing v3
            trackingName="profile_remove_website_link"
            requireFullyVisible={false}
            withVisibilityTracking={true}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default ExternalLinksInput;
